<template>
  <div class="pay-period-selector mb-15 row" :class="fullWidth ? 'full-3columns' : ''">
    <div class="pay-period-frequency-col col-item mb-15" :class="isBiWeekly ? 'col-md-4' : 'col-md-5'">
      <label class="pay-period-label">
        Frequency
      </label>
      <p
        v-if="frequencyHint"
        class="pay-period-hint"
      >
        {{ frequencyHint }}
      </p>
      <input
        type="hidden"
        :name="payPeriodFieldName"
        :value="effectiveValue.payPeriodId"
        readonly
      >
      <AppSelect
        v-tooltip="payPeriodSelectDisabled ? { placement: 'bottom' } : false"
        :data-original-title="payPeriodSelectDisabled ? 'First, set up timesheet approval in order to adjust default setting' : null"
        :value="effectiveValue.payPeriodId"
        :options="payPeriodOptions"
        :select2-options="defaultSelect2Options"
        :disabled="payPeriodSelectDisabled"
        @input="onPayPeriodSelect"
      />
    </div>

    <div
      v-if="showPayPeriodDaySelector"
      class="pay-period-start-col col-md-7 col-item"
    >
      <template v-if="isWeekly">
        <label class="pay-period-label">
          Period start
        </label>
        <p
          v-if="weeklyHint"
          class="pay-period-hint"
        >
          {{ weeklyHint }}
        </p>
        <input
          type="hidden"
          :name="periodWeekDayFieldName"
          :value="effectiveValue.periodWeekDay == null ? '' : effectiveValue.periodWeekDay"
          readonly
        >
        <div class="btn-group-spaced">
          <button
            v-for="day in PAY_PERIOD_DAY_OF_WEEK_OPTIONS"
            :key="day.value"
            type="button"
            class="btn btn-sm btn-default btn-circle"
            :class="{ active: effectiveValue.periodWeekDay === day.value }"
            @click="onPayPeriodWeekDaySelect(day.value)"
          >
            {{ day.label }}
          </button>
        </div>
      </template>
      <template v-if="isBiWeekly">
        <label class="pay-period-label">
          Period start
        </label>
        <p
          v-if="weeklyHint"
          class="pay-period-hint"
        >
          {{ weeklyHint }}
        </p>
        <input
          type="hidden"
          :name="periodWeekDayFieldName"
          :value="effectiveValue.periodWeekDay == null ? '' : effectiveValue.periodWeekDay"
          readonly
        >
        <div class="btn-group-spaced">
          <button
            v-for="day in PAY_PERIOD_DAY_OF_WEEK_OPTIONS"
            :key="day.value"
            type="button"
            class="btn btn-sm btn-default btn-circle"
            :class="{ active: effectiveValue.periodWeekDay === day.value }"
            @click="onPayPeriodWeekDaySelect(day.value)"
          >
            {{ day.label }}
          </button>
        </div>
      </template>

      <template v-if="isMonthly">
        <label class="pay-period-label">
          Period start
        </label>
        <p
          v-if="monthlyHint"
          class="pay-period-hint"
        >
          {{ monthlyHint }}
        </p>
        <input
          type="hidden"
          :name="periodStartDayFieldName"
          :value="effectiveValue.periodStartDay == null ? '' : effectiveValue.periodStartDay"
          readonly
        >
        <AppSelect
          :value="effectiveValue.periodStartDay == null ? '' : effectiveValue.periodStartDay"
          :options="payPeriodDayOfMonthOptions"
          :select2-options="defaultSelect2Options"
          @input="onPayPeriodMonthDaySelect"
        />
      </template>

      <template v-if="isTwicePerMonth">
        <label class="pay-period-label">
          Period start
        </label>
        <p
          v-if="twicePerMonthHint"
          class="pay-period-hint"
        >
          {{ twicePerMonthHint }}
        </p>
        <div class="twice-per-month-select">
          <input
            type="hidden"
            :name="periodStartDayFieldName"
            :value="effectiveValue.periodStartDay == null ? '' : effectiveValue.periodStartDay"
            readonly
          >
          <input
            type="hidden"
            :name="periodSecondStartDayFieldName"
            :value="effectiveValue.periodSecondStartDay == null ? '' : effectiveValue.periodSecondStartDay"
            readonly
          >
          <AppSelect
            :value="selectedDaysOfMonth.map(String)"
            :options="twicePerMonthPeriodDayOptions"
            :select2-options="twicePerMonthSelect2Options"
            @input="onTwicePerMonthDaysInput"
          />
        </div>
      </template>

    </div>

    <div class="clearfix"></div>
    <template v-if="showPayPeriodDaySelector && isBiWeekly">
      <div class="col-item col-md-12">
        <label class="pay-period-label">
          Select Cycle
        </label>
        <div class="biweekly-cycle-options d-flex flex-column align-items-start">
          <label
            v-for="anchor in biWeeklyAnchors"
            :key="anchor.key"
            class="biweekly-cycle-option"
          >
            <input
              type="radio"
              :name="periodAnchorDateFieldName || 'period-anchor-date'"
              :value="anchor.value"
              :checked="effectiveValue.periodAnchorDate === anchor.value"
              @change="onPeriodAnchorSelect(anchor.value)"
            >
            <span>
              <span class="is-regular">{{ anchor.title }}</span> {{ ' ' }}
              <span class="is-regular small text-lighter">({{ anchor.text }})</span>
            </span>
          </label>
        </div>
      </div>
      <div class="clearfix hidden-3columns"></div>
    </template>
    <div
      v-if="showInfoAlert"
      class="pay-period-info-alert-wrapper col-md-12 mt-15"
    >
      <div class="pay-period-info-alert">
        <i class="hi hi-info-filled" />
        <span v-html="infoText" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import {
  BIWEEKLY_PAY_PERIOD,
  DEFAULT_PERIOD_WEEK_DAY,
  MONTHLY_PAY_PERIOD,
  PAY_PERIOD_DAY_OF_MONTH_OPTIONS,
  PAY_PERIOD_DAY_OF_WEEK_OPTIONS,
  TWICE_PER_MONTH_PAY_PERIOD,
  WEEKLY_PAY_PERIOD,
} from '~/app_constants';

const PAY_PERIOD_TYPE_MAP = {
  [WEEKLY_PAY_PERIOD]: 'weekly',
  [TWICE_PER_MONTH_PAY_PERIOD]: 'twice_per_month',
  [BIWEEKLY_PAY_PERIOD]: 'biweekly',
  [MONTHLY_PAY_PERIOD]: 'monthly',
  '4': 'external',
};

// Fixed reference date for biweekly cycle parity. Must match UserOrganization::BIWEEKLY_CYCLE_PARITY_EPOCH.
const WEEK_PARITY_EPOCH = moment.utc('2020-01-06');

function isEvenWeekParity(momentDate) {
  const dateOnly = moment.utc(momentDate.format('YYYY-MM-DD'));

  return Math.floor(dateOnly.diff(WEEK_PARITY_EPOCH, 'days') / 7) % 2 === 0;
}

export default {
  name: 'PayPeriodCustomDatesSelector',
  props: {
    member: {
      type: Object,
      default: null,
    },
    memberInitialState: {
      type: Object,
      default: null,
    },
    value: {
      type: Object,
      default: null,
    },
    initialState: {
      type: Object,
      default: null,
    },
    defaultState: {
      type: Object,
      default: () => ({}),
    },
    payPeriodOptions: {
      type: Array,
      required: true,
    },
    payPeriodSelectDisabled: {
      type: Boolean,
      default: false,
    },
    frequencyHint: {
      type: String,
      default: null,
    },
    weeklyHint: {
      type: String,
      default: null,
    },
    monthlyHint: {
      type: String,
      default: null,
    },
    twicePerMonthHint: {
      type: String,
      default: null,
    },
    fieldPrefix: {
      type: String,
      default: null,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    surface: {
      type: String,
      default: null,
    },
    memberId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      PAY_PERIOD_DAY_OF_WEEK_OPTIONS,
      PAY_PERIOD_DAY_OF_MONTH_OPTIONS,
      defaultSelect2Options: {
        allowClear: false,
        multiple: false,
        width: '100%',
      },
    };
  },
  computed: {
    biWeeklyAnchors() {
      if (this.effectiveValue.periodWeekDay == null) {
        return [];
      }

      const fmt = 'MMM D';
      const selectedWeekDay = moment().startOf('isoWeek').add(Number(this.effectiveValue.periodWeekDay), 'days');
      const followingSelectedWeekDay = selectedWeekDay.clone().add(7, 'days');
      const selectedWeekDayIsCycleB = isEvenWeekParity(selectedWeekDay);
      const cycleA = selectedWeekDayIsCycleB ? followingSelectedWeekDay : selectedWeekDay;
      const cycleB = selectedWeekDayIsCycleB ? selectedWeekDay : followingSelectedWeekDay;

      const buildLabel = (start) => {
        const d1 = start.format(fmt);
        const d2 = start.clone().add(14, 'days').format(fmt);
        const d3 = start.clone().add(28, 'days').format(fmt);

        return `Period starts: ${d1}, then ${d2}, ${d3}`;
      };

      return [
        { value: cycleA.format('YYYY-MM-DD'), key: 'w1', title: 'Cycle A', text: buildLabel(cycleA) },
        { value: cycleB.format('YYYY-MM-DD'), key: 'w2', title: 'Cycle B', text: buildLabel(cycleB) },
      ];
    },
    /** Stable while weekday/tz/today-derived anchors do not change — avoids spurious deep watches on new array refs. */
    biWeeklyAnchorValuesKey() {
      if (this.currentPayPeriodType !== 'biweekly') {return '';}

      return this.biWeeklyAnchors.map((a) => a.value).join('|');
    },
    effectiveValue() {
      if (this.member) {return buildValueFromMember(this.member);}

      const base = this.value ?? {
        payPeriodId: WEEKLY_PAY_PERIOD,
        previousPayPeriodId: WEEKLY_PAY_PERIOD,
        periodWeekDay: DEFAULT_PERIOD_WEEK_DAY,
        periodStartDay: null,
        periodSecondStartDay: null,
        periodAnchorDate: null,
      };

      return { ...base, ...(this.value || {}) };
    },
    effectiveInitialState() {
      if (this.memberInitialState) {return buildInitialStateFromMember(this.memberInitialState);}

      return this.initialState ?? {
        initial_pay_period: WEEKLY_PAY_PERIOD,
        initial_period_week_day: null,
        initial_period_start_day: null,
        initial_period_second_start_day: null,
        initial_period_anchor_date: null,
        initial_twice_per_month_has_selection: false,
      };
    },
    currentPayPeriodType() {
      return PAY_PERIOD_TYPE_MAP[this.effectiveValue.payPeriodId] || null;
    },
    payPeriodFieldName() {
      return this.fieldPrefix ? `${this.fieldPrefix}[pay_period]` : null;
    },
    periodWeekDayFieldName() {
      return this.fieldPrefix ? `${this.fieldPrefix}[period_week_day]` : null;
    },
    periodStartDayFieldName() {
      return this.fieldPrefix ? `${this.fieldPrefix}[period_start_day]` : null;
    },
    periodSecondStartDayFieldName() {
      return this.fieldPrefix ? `${this.fieldPrefix}[period_second_start_day]` : null;
    },
    periodAnchorDateFieldName() {
      return this.fieldPrefix ? `${this.fieldPrefix}[period_anchor_date]` : null;
    },
    isWeeklyOrBiweekly() {
      return this.currentPayPeriodType === 'weekly' || this.currentPayPeriodType === 'biweekly';
    },
    isWeekly() {
      return this.currentPayPeriodType === 'weekly';
    },
    isBiWeekly() {
      return this.currentPayPeriodType === 'biweekly';
    },
    isMonthly() {
      return this.currentPayPeriodType === 'monthly';
    },
    isTwicePerMonth() {
      return this.currentPayPeriodType === 'twice_per_month';
    },
    showPayPeriodDaySelector() {
      return ['weekly', 'biweekly', 'monthly', 'twice_per_month'].includes(this.currentPayPeriodType);
    },
    payPeriodDayOfMonthOptions() {
      return [{ id: '', text: '' }].concat(
        PAY_PERIOD_DAY_OF_MONTH_OPTIONS.map((option) => ({ id: String(option.value), text: option.text })),
      );
    },
    selectedDaysOfMonth() {
      return [this.effectiveValue.periodStartDay, this.effectiveValue.periodSecondStartDay].filter((day) => day != null);
    },
    twicePerMonthPeriodDayOptions() {
      const selectedDays = this.selectedDaysOfMonth;
      const atMaxSelection = selectedDays.length >= 2;

      return [
        { id: '', text: '' },
        ...PAY_PERIOD_DAY_OF_MONTH_OPTIONS.map((option) => ({
          id: String(option.value),
          text: option.text,
          disabled: atMaxSelection && !selectedDays.includes(option.value),
        })),
      ];
    },
    twicePerMonthSelect2Options() {
      return {
        multiple: true,
        placeholder: 'Select dates',
        allowClear: true,
        width: '100%',
      };
    },
    hasChangedPayPeriodDays() {
      const initialPayPeriod = this.effectiveInitialState.initial_pay_period ?? '';

      if (this.effectiveValue.payPeriodId !== initialPayPeriod) {return true;}

      if (this.isWeeklyOrBiweekly) {
        if (this.effectiveValue.periodWeekDay !== this.effectiveInitialState.initial_period_week_day) {return true;}

        if (this.isBiWeekly) {
          return this.effectiveValue.periodAnchorDate !== (this.effectiveInitialState.initial_period_anchor_date ?? null);
        }

        return false;
      }
      if (this.isMonthly) {
        return this.effectiveValue.periodStartDay !== this.effectiveInitialState.initial_period_start_day;
      }
      if (this.isTwicePerMonth) {
        return this.effectiveValue.periodStartDay !== this.effectiveInitialState.initial_period_start_day ||
          this.effectiveValue.periodSecondStartDay !== this.effectiveInitialState.initial_period_second_start_day;
      }

      return false;
    },
    infoDescriptor() {
      if (!this.currentPayPeriodType || this.currentPayPeriodType === 'external') {return null;}

      if (this.isWeeklyOrBiweekly) {
        if (this.effectiveValue.periodWeekDay == null) {return null;}
        const day = PAY_PERIOD_DAY_OF_WEEK_OPTIONS.find((d) => d.value === this.effectiveValue.periodWeekDay);

        if (!day) {return null;}

        if (this.isBiWeekly) {
          const anchor = this.effectiveValue.periodAnchorDate &&
            moment.utc(this.effectiveValue.periodAnchorDate).format('ddd, D MMM, YYYY');

          return {
            payPeriodType: this.currentPayPeriodType,
            frequencyLabel: 'bi-weekly',
            dayOrDate: anchor || day.name,
          };
        }

        return { payPeriodType: this.currentPayPeriodType, frequencyLabel: 'weekly', dayOrDate: day.name };
      }

      if (this.isMonthly) {
        if (this.effectiveValue.periodStartDay == null) {return null;}
        const opt = PAY_PERIOD_DAY_OF_MONTH_OPTIONS.find((o) => o.value === this.effectiveValue.periodStartDay);

        if (!opt) {return null;}

        return { payPeriodType: this.currentPayPeriodType, frequencyLabel: 'monthly', dayOrDate: opt.text };
      }

      if (this.isTwicePerMonth) {
        if (this.effectiveValue.periodStartDay == null || this.effectiveValue.periodSecondStartDay == null) {return null;}
        const opt1 = PAY_PERIOD_DAY_OF_MONTH_OPTIONS.find((o) => o.value === this.effectiveValue.periodStartDay);
        const opt2 = PAY_PERIOD_DAY_OF_MONTH_OPTIONS.find((o) => o.value === this.effectiveValue.periodSecondStartDay);

        if (!opt1 || !opt2) {return null;}

        return { payPeriodType: this.currentPayPeriodType, frequencyLabel: 'twice per month', dayOrDate: `${opt1.text.toLowerCase()} and ${opt2.text.toLowerCase()}` };
      }

      return null;
    },
    nextPeriodStartDate() {
      if (!this.infoDescriptor) {return null;}

      const today = moment();
      const fmt = 'MMM D, YYYY';
      const setDay = (m, day) => (day === 31 ? m.endOf('month') : m.date(day));
      const type = this.infoDescriptor.payPeriodType;

      if ((type === 'weekly' || type === 'biweekly') && this.effectiveValue.periodWeekDay != null) {
        const jsDay = this.effectiveValue.periodWeekDay === 6 ? 0 : this.effectiveValue.periodWeekDay + 1;
        const next = moment(today).day(jsDay);

        if (next.isSameOrBefore(today, 'day')) {next.add(7, 'days');}

        return next.format(fmt);
      }

      if (type === 'monthly' && this.effectiveValue.periodStartDay != null) {
        let next = setDay(moment(today), this.effectiveValue.periodStartDay);

        if (next.isSameOrBefore(today, 'day')) {
          next = setDay(next.add(1, 'month'), this.effectiveValue.periodStartDay);
        }

        return next.format(fmt);
      }

      if (type === 'twice_per_month' && this.effectiveValue.periodStartDay != null && this.effectiveValue.periodSecondStartDay != null) {
        const lastDayOfMonth = moment(today).endOf('month').date();
        const actualD1 = this.effectiveValue.periodStartDay === 31 ? lastDayOfMonth : this.effectiveValue.periodStartDay;
        const actualD2 = this.effectiveValue.periodSecondStartDay === 31 ? lastDayOfMonth : this.effectiveValue.periodSecondStartDay;
        const d1 = Math.min(actualD1, actualD2);
        const d2 = Math.max(actualD1, actualD2);
        const dayOfMonth = today.date();
        let next;

        if (dayOfMonth < d1) {
          next = setDay(moment(today), this.effectiveValue.periodStartDay);
        } else if (dayOfMonth < d2) {
          next = setDay(moment(today), this.effectiveValue.periodSecondStartDay);
        } else {
          next = setDay(moment(today).add(1, 'month'), this.effectiveValue.periodStartDay);
        }

        return next.format(fmt);
      }

      return null;
    },
    showInfoAlert() {
      return this.hasChangedPayPeriodDays && this.infoDescriptor != null && this.infoText !== '';
    },
    infoText() {
      if (!this.infoDescriptor) {return '';}
      const frequency = `<strong>${this.infoDescriptor.frequencyLabel}</strong>`;
      const dayOrDate = `<strong>${this.infoDescriptor.dayOrDate}</strong>`;
      const startDate = this.nextPeriodStartDate ? `<strong>${this.nextPeriodStartDate}</strong>` : '';

      if (this.isBiWeekly) {
        const anchorDateStr = this.effectiveValue.periodAnchorDate;

        if (!anchorDateStr || !startDate) {
          return '';
        }

        const anchorMoment = moment(anchorDateStr);
        const cycleStr = this.biWeeklyAnchors.find((a) => a.value === anchorDateStr)?.title || '';
        const dayName = anchorMoment.format('dddd');
        const startDateLabel = anchorMoment.format('MMM D, YYYY');

        return `When saved, timesheets will run ${frequency} <strong>on ${cycleStr}</strong>, every other <strong>${dayName}</strong>, starting <strong>${startDateLabel}</strong>.`;
      }

      return `When saved, timesheets will run ${frequency}, starting every ${dayOrDate}${startDate ? `. The next timesheet will start on ${startDate}` : ''}.`;
    },
  },
  watch: {
    biWeeklyAnchorValuesKey: {
      immediate: true,
      handler(key) {
        if (!key) {return;}

        const anchors = this.biWeeklyAnchors;

        if (!anchors?.length) {return;}

        const current = this.effectiveValue.periodAnchorDate;
        const valid = anchors.some((a) => a.value === current);

        if (!current || !valid) {
          const nextAnchor = this.isInAnchorRange(anchors[0].value, current) ? anchors[0].value : anchors[1].value;

          this.emitState({ ...this.effectiveValue, periodAnchorDate: nextAnchor });
        }
      },
    },
  },
  created() {
    const hasInvalidBiweeklyAnchor = this.isBiWeekly && this.biWeeklyAnchors.length > 0 &&
      !this.biWeeklyAnchors.some((anchor) => anchor.value === this.effectiveValue.periodAnchorDate);

    if (!hasInvalidBiweeklyAnchor) {
      this.emitState(this.effectiveValue);
    }
  },
  mounted() {
    this.isReady = true;

    if (!this.surface) { return; }

    this.setupVisibilityTracking();

    this._savedHandler = () => { this.formSaved = true; };
    window.addEventListener('pay-period:saved', this._savedHandler);

    this.abandonedHandler = () => {
      if (this.formSaved || !this.wasChanged) { return; }
      this.trackAbandoned();
    };
    window.addEventListener('beforeunload', this.abandonedHandler);
  },
  beforeDestroy() {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }
    window.removeEventListener('pay-period:saved', this._savedHandler);
    window.removeEventListener('beforeunload', this.abandonedHandler);
  },
  methods: {
    setupVisibilityTracking() {
      if (typeof IntersectionObserver === 'undefined') {
        this.trackViewed();

        return;
      }

      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.trackViewed();
            this.visibilityObserver.disconnect();
          }
        },
        { threshold: 0.01 },
      );
      this.visibilityObserver.observe(this.$el);
    },
    trackViewed() {
      window.analytics?.track('Pay period viewed', {
        page_url: window.location.href,
        organization_id: document.body.getAttribute('data-organization-id'),
        member_id: this.memberId,
        surface: this.surface,
      });
    },
    trackAbandoned() {
      window.analytics?.track('Pay period abandoned', {
        page_url: window.location.href,
        organization_id: document.body.getAttribute('data-organization-id'),
        member_id: this.memberId,
        surface: this.surface,
        pay_period_config: {
          frequency: PAY_PERIOD_TYPE_MAP[this.effectiveValue.payPeriodId] ?? null,
          week_day: this.effectiveValue.periodWeekDay ?? null,
          anchor_date: this.effectiveValue.periodAnchorDate ?? null,
          start_day: this.effectiveValue.periodStartDay ?? null,
          second_start_day: this.effectiveValue.periodSecondStartDay ?? null,
        },
      });
    },
    emitState(nextState) {
      this.$emit('input', {
        payPeriodId: nextState.payPeriodId,
        previousPayPeriodId: nextState.previousPayPeriodId,
        periodWeekDay: nextState.periodWeekDay,
        periodStartDay: nextState.periodStartDay,
        periodSecondStartDay: nextState.periodSecondStartDay,
        periodAnchorDate: nextState.periodAnchorDate,
      });

      if (this.isReady) {
        this.wasChanged = true;
      }
    },
    isInAnchorRange(newAnchorDate, currentAnchorDate) {
      if (!newAnchorDate || !currentAnchorDate) {
        return false;
      }

      const newAnchorStart = moment.utc(newAnchorDate).startOf('isoWeek');
      const newAnchorEnd = newAnchorStart.clone().add(6, 'days');
      const currentDate = moment.utc(currentAnchorDate);

      return currentDate.isBetween(newAnchorStart, newAnchorEnd, 'day', '[]');
    },
    onPeriodAnchorSelect(isoDate) {
      this.emitState({ ...this.effectiveValue, periodAnchorDate: isoDate });
    },
    onPayPeriodSelect(payPeriodId) {
      const nextId = payPeriodId == null ? '' : String(payPeriodId);
      const previousId = this.effectiveValue.previousPayPeriodId;
      const payPeriodChanged = previousId !== nextId;

      const nextState = {
        payPeriodId: nextId,
        previousPayPeriodId: nextId,
        periodWeekDay: this.effectiveValue.periodWeekDay,
        periodStartDay: this.effectiveValue.periodStartDay,
        periodSecondStartDay: this.effectiveValue.periodSecondStartDay,
        periodAnchorDate: this.effectiveValue.periodAnchorDate,
      };

      if (payPeriodChanged) {
        const nextType = PAY_PERIOD_TYPE_MAP[nextId];

        if (nextType === 'twice_per_month') {
          if (this.effectiveInitialState.initial_twice_per_month_has_selection) {
            nextState.periodStartDay = this.effectiveInitialState.initial_period_start_day ?? null;
            nextState.periodSecondStartDay = this.effectiveInitialState.initial_period_second_start_day ?? null;
          } else {
            nextState.periodStartDay = 1;
            nextState.periodSecondStartDay = 16;
          }
        } else {
          nextState.periodStartDay = null;
          nextState.periodSecondStartDay = null;
        }

        if (nextType === 'weekly' || nextType === 'biweekly') {
          nextState.periodWeekDay = this.effectiveInitialState.initial_period_week_day ?? this.defaultState.period_week_day ?? null;
        } else if (nextType === 'monthly') {
          nextState.periodStartDay = this.effectiveInitialState.initial_period_start_day ?? this.defaultState.period_start_day ?? null;
          nextState.periodSecondStartDay = null;
        }

        if (nextType !== 'biweekly') {
          nextState.periodAnchorDate = null;
        }
      }

      this.emitState(nextState);
    },
    onPayPeriodWeekDaySelect(dayValue) {
      const next = { ...this.effectiveValue, periodWeekDay: dayValue };

      if (PAY_PERIOD_TYPE_MAP[this.effectiveValue.payPeriodId] !== 'biweekly') {
        next.periodAnchorDate = null;
      }

      this.emitState(next);
    },
    onPayPeriodMonthDaySelect(dayValue) {
      this.emitState({
        ...this.effectiveValue,
        periodStartDay: dayValue === '' ? null : Number(dayValue),
      });
    },
    onTwicePerMonthDaysInput(idsOrNumbers) {
      const values = Array.isArray(idsOrNumbers) ? idsOrNumbers : (idsOrNumbers == null ? [] : [idsOrNumbers]);
      const selectedDays = values
        .map((v) => Number(v))
        .filter((v) => Number.isInteger(v) && v > 0)
        .sort((a, b) => a - b)
        .slice(0, 2);

      this.emitState({
        ...this.effectiveValue,
        periodStartDay: selectedDays[0] ?? null,
        periodSecondStartDay: selectedDays[1] ?? null,
      });
    },
  },
};

export function buildValueFromMember(member) {
  const payPeriodId = member.pay_period != null ? String(member.pay_period) : '';

  return {
    payPeriodId,
    previousPayPeriodId: member._previousPayPeriodId ?? payPeriodId,
    periodWeekDay: member.period_week_day,
    periodStartDay: member.period_start_day,
    periodSecondStartDay: member.period_second_start_day,
    periodAnchorDate: member.period_anchor_date,
  };
}

export function buildInitialStateFromMember(member) {
  const payPeriodId = member.pay_period != null ? String(member.pay_period) : '';

  return {
    initial_pay_period: payPeriodId,
    initial_period_week_day: member.period_week_day,
    initial_period_start_day: member.period_start_day,
    initial_period_second_start_day: member.period_second_start_day,
    initial_period_anchor_date: member.initial_period_anchor_date,
    initial_twice_per_month_has_selection: payPeriodId === TWICE_PER_MONTH_PAY_PERIOD,
  };
}
</script>
